import api from "@/api/axios";
import type { MappedProfile, UserProfile } from "@/types/user.types";

// ============================================
// Data Mapping - snake_case → camelCase
// ============================================

const mapProfileResponse = (raw: UserProfile): MappedProfile => {
  // Prefer `customer_levels` array from new BE response;
  // fallback to deprecated `level` field for backward compat.
  const currentCustomerLevel = raw.customer_levels?.find(
    (cl) => cl.is_current,
  );
  const activeLevel = currentCustomerLevel?.level ?? raw.level ?? null;

  return {
    id: raw.id,
    fullName: raw.full_name,
    email: raw.email,
    phone: raw.phone,
    profileImageUrl: raw.profile_image_url,
    isActive: raw.is_active,
    birthday: raw.birthday,
    referralCode: raw.referral_code,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    level: activeLevel
      ? {
          id: activeLevel.id,
          name: activeLevel.name,
          rankPriority: activeLevel.rank_priority,
          targetSpending: activeLevel.target_spending,
          earningRate: activeLevel.earning_rate,
          birthdayBonusPoints: activeLevel.birthday_bonus_points,
          icon: activeLevel.icon,
        }
      : null,
    customerLevels: (raw.customer_levels ?? []).map((cl) => ({
      id: cl.id,
      levelId: cl.level_id,
      achievedAt: cl.achieved_at,
      isCurrent: cl.is_current,
      level: {
        id: cl.level.id,
        name: cl.level.name,
        rankPriority: cl.level.rank_priority,
        targetSpending: cl.level.target_spending,
        earningRate: cl.level.earning_rate,
        birthdayBonusPoints: cl.level.birthday_bonus_points,
        icon: cl.level.icon,
      },
    })),
    customer: {
      id: raw.id,
      currentPoints: raw.current_points || 0,
      totalEarnPoints: raw.total_earned_points || 0,
      totalSpentPoints: raw.total_spent_points || 0,
    },
    pointHistory: (raw.point_history || []).map((ph) => ({
      id: ph.id,
      pointChange: ph.point_change,
      type: ph.type,
      createdAt: ph.created_at,
    })),
  };
};


export interface UpdatePasswordData {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

// ============================================
// User Profile Service
// ============================================

/**
 * Unified user profile service for both customer and loyalty contexts.
 * Both use the same /auth/me endpoint which returns user profile with
 * level, customer wallet, and point history relations.
 */
export const userService = {
  /**
   * Get user profile for the authenticated user.
   * GET /auth/me → returns User object with nested relations
   * (level + customer wallet + point_history) loaded via JWT strategy.
   */
  async getProfile(): Promise<MappedProfile> {
    const response = await api.get<UserProfile>("/auth/me");
    return mapProfileResponse(response.data);
  },

  /**
   * Update text fields of the user profile.
   * PUT /profile
   */
  async updateProfile(data: {
    fullName?: string;
    email?: string;
    phone?: string;
    birthday?: string;
  }): Promise<MappedProfile> {
    const payload = {
      full_name: data.fullName,
      email: data.email,
      phone: data.phone,
      birthday: data.birthday,
    };
    const response = await api.put("/profile", payload);
    return mapProfileResponse(response.data.data);
  },

  /**
   * Upload user avatar.
   * POST /profile/avatar
   */
  async uploadAvatar(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("avatar", file);
    const response = await api.post("/profile/avatar", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data.data.profile_image_url;
  },

  /**
   * Change user password.
   * PATCH /auth/update-password
   */
  async updatePassword(data: UpdatePasswordData): Promise<void> {
    await api.patch("/auth/update-password", data);
  },
};
